/**
 * ClubJoin — public landing page for invite links.
 *
 * Route: /clubs/join/:inviteToken
 *
 * Shows club name, description, member count and a "Request to Join" button.
 * Redirects to login if not authenticated, then returns here after login.
 */
import { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Paper,
  useTheme,
} from '@mui/material';
import {
  Group as GroupIcon,
  CheckCircle as CheckCircleIcon,
  HowToReg as HowToRegIcon,
} from '@mui/icons-material';
import { tokens } from '../theme';
import { useAuth } from '../contexts/AuthContext';
import { getClubPreview, requestToJoin } from '../api/clubApi';

export default function ClubJoin() {
  const { inviteToken } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { isAuthenticated } = useAuth();

  const [club, setClub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [requesting, setRequesting] = useState(false);
  const [requested, setRequested] = useState(false);
  const [requestError, setRequestError] = useState('');

  useEffect(() => {
    getClubPreview(inviteToken)
      .then(setClub)
      .catch((err) => {
        if (err?.response?.status === 404) {
          setError('This invite link is invalid or has expired.');
        } else {
          setError('Failed to load club details.');
        }
      })
      .finally(() => setLoading(false));
  }, [inviteToken]);

  const handleRequestJoin = async () => {
    if (!isAuthenticated) {
      navigate('/login', { state: { from: location.pathname } });
      return;
    }
    setRequesting(true);
    setRequestError('');
    try {
      await requestToJoin(inviteToken);
      setRequested(true);
    } catch (err) {
      const detail = err?.response?.data?.detail || '';
      if (detail === 'Already a member') {
        navigate(`/clubs/${club.id}`);
        return;
      }
      if (detail === 'Join request already pending') {
        setRequested(true);
        return;
      }
      setRequestError(detail || 'Failed to submit join request.');
    } finally {
      setRequesting(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh" p={3}>
        <Alert severity="error" sx={{ maxWidth: 480 }}>{error}</Alert>
      </Box>
    );
  }

  return (
    <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh" p={3}>
      <Paper
        elevation={4}
        sx={{
          maxWidth: 480,
          width: '100%',
          p: 4,
          backgroundColor: theme.palette.mode === 'dark' ? colors.grey[800] : colors.grey[900],
          borderRadius: 2,
          textAlign: 'center',
        }}
      >
        {/* Club color dot */}
        <Box
          sx={{
            width: 56,
            height: 56,
            borderRadius: '50%',
            backgroundColor: club.color || colors.blueAccent[400],
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mx: 'auto',
            mb: 2,
          }}
        >
          <GroupIcon sx={{ color: '#fff', fontSize: 28 }} />
        </Box>

        <Typography variant="h5" fontWeight="bold" color={colors.grey[100]} mb={1}>
          {club.name}
        </Typography>

        {club.description && (
          <Typography variant="body2" color={colors.grey[300]} mb={2}>
            {club.description}
          </Typography>
        )}

        <Typography variant="caption" color={colors.grey[400]} display="block" mb={3}>
          {club.member_count} member{club.member_count !== 1 ? 's' : ''}
        </Typography>

        {requested ? (
          <Box display="flex" flexDirection="column" alignItems="center" gap={1.5}>
            <CheckCircleIcon sx={{ fontSize: 40, color: colors.greenAccent[400] }} />
            <Typography variant="body1" color={colors.greenAccent[400]} fontWeight="bold">
              Request sent!
            </Typography>
            <Typography variant="body2" color={colors.grey[300]}>
              Waiting for admin approval. You'll have access once an admin approves your request.
            </Typography>
            <Button
              variant="outlined"
              onClick={() => navigate('/running')}
              sx={{ mt: 1, textTransform: 'none' }}
            >
              Back to dashboard
            </Button>
          </Box>
        ) : (
          <>
            {requestError && (
              <Alert severity="error" sx={{ mb: 2 }}>{requestError}</Alert>
            )}
            {!isAuthenticated && (
              <Alert severity="info" sx={{ mb: 2 }}>
                You need to log in to join this club.
              </Alert>
            )}
            <Button
              variant="contained"
              size="large"
              startIcon={requesting ? <CircularProgress size={18} color="inherit" /> : <HowToRegIcon />}
              onClick={handleRequestJoin}
              disabled={requesting}
              sx={{ textTransform: 'none', minWidth: 200 }}
            >
              {requesting ? 'Sending request…' : isAuthenticated ? 'Request to Join' : 'Log in to Join'}
            </Button>
          </>
        )}
      </Paper>
    </Box>
  );
}
